import { A, useNavigate } from "@solidjs/router";
import { useBillingClient, useCallOptions, withBearerToken } from "@storytell/cp-client";
import { StButton } from "@storytell/ui/buttons";
import { StPopover, StPopoverTrigger } from "@storytell/ui/popover";
import { TbCalendarEvent, TbHelpCircleFilled, TbLoader2, TbTrendingUp, TbWallet } from "solid-icons/tb";
import HexagonSparkleIcon from "./HexagonSparkleIcon";
import { type Component, Show, Suspense } from "solid-js";
import { useWire } from "~/wire";
import { usePromptBarContext } from "../PromptBarContext";
import { useTooltipBehavior } from "../hooks";
import { PromptBarButton } from "./PromptBarButton";

/**
 * Credits display component with pill and detailed modal
 * Matches the styling of the Settings panel
 */
export const CreditsDisplay: Component = () => {
  const wire = useWire();
  const navigate = useNavigate();
  const { credits, knowledgeSources } = usePromptBarContext();

  // Popover open/close is managed by StPopover; keep pricing/knowledge panel coordination here

  // Calculate percentage used
  const percentageUsed = () => {
    const remaining = wire.billing.credits();
    const used = wire.billing.creditsUsedThisPeriod();
    const total = used + remaining; // Total = used + remaining

    if (total === 0) return 0; // No credits allocated or used at all
    return Math.min(100, Math.round((used / total) * 100));
  };

  // Get color based on usage
  const getUsageColor = () => {
    const percent = percentageUsed();
    if (percent < 50) return "text-emerald-500";
    if (percent < 80) return "text-yellow-500";
    return "text-red-500";
  };

  // Get icon color based on usage
  const getIconColor = () => {
    const percent = percentageUsed();
    if (percent < 60) return "text-primary-text"; // White/default text color
    if (percent < 80) return "text-orange-500";
    return "text-red-500";
  };

  // Get button background classes based on usage
  const getButtonClasses = () => {
    const percent = percentageUsed();
    if (percent < 60) {
      return "bg-panel/80 border-primary-border hover:bg-panel-hover";
    }
    if (percent < 80) {
      return "bg-gradient-to-br from-orange-500/10 to-orange-600/10 border-orange-500/30 hover:from-orange-500/20 hover:to-orange-600/20";
    }
    return "bg-gradient-to-br from-orange-500/10 to-red-600/10 border-red-500/30 hover:from-orange-500/20 hover:to-red-600/20";
  };

  // Get status dot color
  const getStatusDotClasses = () => {
    const percent = percentageUsed();
    if (percent > 80) return "bg-red-500 animate-pulse";
    if (percent > 60) return "bg-orange-500";
    return "bg-emerald-500";
  };

  return (
    <Show when={wire.billing.show()}>
      <StPopover
        noArrow
        open={credits.showTooltip()}
        onOpenChange={(open) => {
          if (open && knowledgeSources.showTooltip()) {
            knowledgeSources.setShowTooltip(false);
          }
          credits.setShowTooltip(open);
        }}
        gutter={10}
        placement="top-end"
        flip
        contentProps={{ class: "w-80 bg-panel border border-primary-border rounded-xl shadow-xl" }}
        content={
          <Suspense fallback={<div class="p-4">Loading...</div>}>
            <div class="p-4 space-y-3">
              {/* Header */}
              <div class="flex items-center justify-between">
                <span class="text-sm font-medium text-primary-text">
                  {wire.billing.isProPlan() ? "Pro Plan" : "Explore Plan (Free)"}
                </span>
                <button
                  type="button"
                  class="text-xs text-brand underline hover:underline-offset-2 cursor-pointer focus:outline-none"
                  onClick={async () => {
                    credits.hideTooltip();
                    if (wire.billing.isFreePlan()) {
                      navigate("/pricing");
                      return;
                    }

                    const client = useBillingClient();
                    const res = await client.createPortalSession(
                      {
                        returnUrl: `https://${APP_DOMAIN}/`,
                        customerId: wire.auth.user()?.id,
                      },
                      useCallOptions(withBearerToken(wire.auth.token())),
                    );
                    if (res.isOk() && res.value.session.url) {
                      window.location.href = res.value.session.url;
                    }
                  }}
                >
                  {wire.billing.isProPlan() ? "Manage" : "Upgrade"}
                </button>
              </div>

              <div class="pb-3 border-b border-primary-border space-y-3">
                {/* Credits Balance */}
                <div class="flex items-start gap-3">
                  <TbWallet class={`w-5 h-5 ${getUsageColor()} mt-0.5 flex-shrink-0`} />
                  <div class="flex-1">
                    <div class="text-sm font-medium text-primary-text">Credits Balance</div>
                    <div class="text-xs text-secondary-text mt-0.5">
                      <span class="font-bold text-primary-text">{wire.billing.credits().toLocaleString()}</span> credits
                      available
                    </div>
                  </div>
                </div>

                {/* Usage This Month */}
                <div class="flex items-start gap-3">
                  <TbTrendingUp class="w-5 h-5 text-blue-500 mt-0.5 flex-shrink-0" />
                  <div class="flex-1">
                    <div class="text-sm font-medium text-primary-text">Usage This Month</div>
                    <div class="text-xs text-secondary-text mt-0.5">
                      <span class="font-bold text-primary-text">
                        {wire.billing.creditsUsedThisPeriod().toLocaleString()}
                      </span>{" "}
                      credits used ({percentageUsed()}%)
                    </div>
                    <div class="mt-2 h-2 bg-muted/30 rounded-full overflow-hidden">
                      <div
                        class={`h-full ${
                          percentageUsed() < 50
                            ? "bg-emerald-500"
                            : percentageUsed() < 80
                              ? "bg-yellow-500"
                              : "bg-red-500"
                        } transition-all duration-300`}
                        style={{ width: `${percentageUsed()}%` }}
                      />
                    </div>
                  </div>
                </div>

                {/* Pro Plan Info */}
                <Show when={wire.billing.isProPlan()}>
                  <div class="flex items-start gap-3">
                    <TbCalendarEvent class="w-5 h-5 text-purple-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <div class="text-sm font-medium text-primary-text">Pro Plan</div>
                      <div class="text-xs text-secondary-text mt-0.5">Monthly renewal with additional credits</div>
                    </div>
                  </div>
                </Show>
              </div>

              <div class="space-y-2 pt-1">
                <Show
                  when={!wire.auth.isGuest()}
                  fallback={
                    <button
                      type="button"
                      onClick={() => navigate("/auth/signup")}
                      class="w-full px-3 py-2 bg-brand text-white rounded-lg hover:bg-brand/90 transition-colors text-sm font-medium"
                    >
                      Sign up to get {(10000).toLocaleString()} free credits
                    </button>
                  }
                >
                  <button
                    type="button"
                    onClick={() => navigate("/pricing")}
                    class="w-full px-3 py-2 bg-brand text-white rounded-lg hover:bg-brand/90 transition-colors text-sm font-medium"
                  >
                    Top up
                  </button>
                </Show>

                <a
                  class="inline-flex items-center gap-1 text-xs text-brand underline hover:underline-offset-2"
                  href="https://go.storytell.ai/learn-credits"
                  target="_blank"
                  rel="noreferrer"
                >
                  <TbHelpCircleFilled class="w-3.5 h-3.5" />
                  Learn how credits work
                </a>
              </div>
            </div>
          </Suspense>
        }
      >
        <span class="relative">
          <StPopoverTrigger
            as={PromptBarButton}
            icon={HexagonSparkleIcon}
            iconClass={`${getIconColor()} transition-all duration-300 group-hover/btn:scale-interaction group-active/btn:scale-active ${percentageUsed() > 80 ? "animate-pulse" : ""}`}
            class={`${getButtonClasses()} transition-all duration-300`}
            aria-label="Credits"
          />
          <div class={`absolute top-0 right-0 w-2.5 h-2.5 rounded-full ${getStatusDotClasses()} pointer-events-none`} />
        </span>
      </StPopover>
    </Show>
  );
};
